import {
  FREQUENCIES,
  FREQUENCY_LABELS,
  RATE_SCALE,
  invertRate,
  convert,
  formatPercent,
  impliedRate,
  monthOf,
  parseAmount,
  parseRate,
  type AccountDto,
  type AccountKind,
  type CategoryRef,
  type Flag,
  type Frequency,
  type PlanCategoryDto,
  type PlanGroupDto,
  type ScheduledDto,
  type TransactionDto,
} from '@koverta/shared';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type Ref,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { minorToRaw, rawToMinor } from '../components/Keypad';
import { useToast } from '../components/toast';
import {
  Avatar,
  CatPill,
  CurrencyChip,
  Dialog,
  FLAG_COLORS,
  Flag as FlagIcon,
  Icon,
  Kbd,
  MoneyInput,
  Seg,
  Sheet,
} from '../components/ui';
import { del, errorMessage, patch, post, put } from '../lib/api';
import { useClickOutside } from '../lib/hooks';
import { useHousehold, type Fmt } from '../lib/household';
import { sendOrQueue } from '../lib/outbox';
import { invalidateScopes, useAccounts, usePayees, usePlan, useRates } from '../lib/queries';
import { CategoryPicker } from './Inbox';
import '../styles/scheduled.css';

type Dir = 'out' | 'in' | 'xfer';
interface SplitRow {
  categoryId: CategoryRef;
  amount: string;
  memo: string;
}

interface FormInit {
  accountId?: string;
  tx?: TransactionDto;
  mode?: Dir;
  /** Edit a scheduled series instead of a transaction ("Edit series", scheduled-transactions.md §5). */
  schedule?: ScheduledDto;
}

/** A schedule's From/To orientation: money leaves the schedule's own account when its amount is negative. */
function scheduleSides(sc: ScheduledDto | undefined) {
  if (!sc) return null;
  const out = sc.amount < 0;
  const self = Math.abs(sc.amount);
  const other = sc.transfer ? (sc.transfer.amount ?? self) : 0;
  if (!sc.transfer) return { from: sc.accountId, to: '', fromAmount: self, toAmount: 0 };
  return out
    ? { from: sc.accountId, to: sc.transfer.accountId, fromAmount: self, toAmount: other }
    : { from: sc.transfer.accountId, to: sc.accountId, fromAmount: other, toAmount: self };
}

/** Form state shared by the desktop dialog and the mobile page (screens.md §5). */
function useTxForm({ accountId, tx, mode, schedule: sc }: FormInit) {
  const { fmt, today, member } = useHousehold();
  const accounts = (useAccounts().data ?? []).filter((a) => !a.closed || a.id === tx?.accountId);
  const rates = useRates().data;
  const budgetAccounts = accounts.filter((a) => a.onBudget);
  const lastAccount = localStorage.getItem('koverta.lastAccount');
  const sides = scheduleSides(sc);
  const initialAccount =
    tx?.accountId ??
    sides?.from ??
    accountId ??
    (lastAccount && accounts.some((a) => a.id === lastAccount) ? lastAccount : undefined) ??
    budgetAccounts[0]?.id ??
    accounts[0]?.id ??
    '';
  const [dir, setDir] = useState<Dir>(
    tx
      ? tx.transfer
        ? 'xfer'
        : tx.amount > 0
          ? 'in'
          : 'out'
      : sc
        ? sc.transfer
          ? 'xfer'
          : sc.amount > 0
            ? 'in'
            : 'out'
        : (mode ?? 'out'),
  );
  const [acctId, setAcctId] = useState(initialAccount);
  const account = accounts.find((a) => a.id === acctId);
  const [raw, setRaw] = useState(
    tx
      ? minorToRaw(Math.abs(tx.transfer && tx.amount > 0 ? tx.transfer.otherAmount : tx.amount))
      : sides
        ? minorToRaw(sides.fromAmount)
        : '',
  );
  const [toId, setToId] = useState(
    tx?.transfer ? (tx.amount < 0 ? tx.transfer.otherAccountId : tx.accountId) : (sides?.to ?? ''),
  );
  const [toRaw, setToRaw] = useState(
    tx?.transfer
      ? minorToRaw(Math.abs(tx.amount < 0 ? tx.transfer.otherAmount : tx.amount))
      : sc?.transfer && sides
        ? minorToRaw(sides.toAmount)
        : '',
  );
  const [payee, setPayee] = useState(
    tx && !tx.transfer ? tx.payeeName : sc && !sc.transfer ? sc.payeeName : '',
  );
  const [categoryId, setCategoryId] = useState<CategoryRef>(
    tx ? tx.categoryId : sc ? sc.categoryId : mode === 'in' ? 'rta' : null,
  );
  const [date, setDate] = useState(tx?.date ?? sc?.dateNext ?? today);
  const [memo, setMemo] = useState(tx?.memo ?? sc?.memo ?? '');
  const [flag, setFlag] = useState<Flag | null>(tx?.flag ?? sc?.flag ?? null);
  const initialSplits = tx?.splits.length ? tx.splits : sc?.splits.length ? sc.splits : null;
  const [splits, setSplits] = useState<SplitRow[] | null>(
    initialSplits
      ? initialSplits.map((s) => ({
          categoryId: s.categoryId,
          amount: fmt.num(Math.abs(s.amount), 2),
          memo: s.memo,
        }))
      : null,
  );
  const [freq, setFreq] = useState<Frequency>(sc?.frequency ?? 'never');
  const [endDate, setEndDate] = useState(sc?.endDate ?? '');
  // New entries with a repeat, or dated in the future, are saved as a schedule (YNAB behaviour).
  const isSchedule = !!sc || (!tx && (freq !== 'never' || date > today));
  const [rateText, setRateText] = useState(
    tx?.rateSource === 'manual' ? fmt.rate(account?.currency === 'EUR' ? tx.rate : invertRate(tx.rate)) : '',
  );
  const [ovOpen, setOvOpen] = useState(tx?.rateSource === 'manual');
  const plan = usePlan(monthOf(date));

  // Fix the transfer leg orientation when editing the receiving side.
  useEffect(() => {
    if (tx?.transfer && tx.amount > 0) {
      setAcctId(tx.transfer.otherAccountId);
      setToId(tx.accountId);
    }
  }, [tx]);

  const amount = rawToMinor(raw);
  const signed = dir === 'in' ? amount : -amount;
  const nbsRate = rates?.current.rate ?? 1_171_500;
  const manualRate = ovOpen && rateText ? parseRate(rateText, fmt.locale) : null;
  const rateForAccount = (a: AccountDto | undefined, r: number) =>
    !a || a.currency === fmt.base ? RATE_SCALE : a.currency === 'EUR' ? r : invertRate(r);
  const effectiveRate = manualRate ?? nbsRate;
  const base = account ? convert(signed, rateForAccount(account, effectiveRate)) : signed;
  const to = accounts.find((a) => a.id === toId);
  const toAmount = to && account && to.currency === account.currency ? amount : rawToMinor(toRaw);
  const groups = useMemo(
    () =>
      (plan.data?.groups ?? [])
        .filter((g) => !g.hidden)
        .map((g) => ({ ...g, categories: g.categories.filter((c) => !c.hidden) })),
    [plan.data],
  );
  const catById = useMemo(() => new Map(groups.flatMap((g) => g.categories).map((c) => [c.id, c])), [groups]);
  const splitTotal = splits ? splits.reduce((s, r) => s + (parseAmount(r.amount, fmt.locale) ?? 0), 0) : 0;

  const onPickAccount = (id: string) => {
    setAcctId(id);
    setOvOpen(false);
    setRateText('');
  };

  const reset = () => {
    setRaw('');
    setToRaw('');
    setPayee('');
    setMemo('');
    setFlag(null);
    setSplits(null);
    setOvOpen(false);
    setRateText('');
    setCategoryId(dir === 'in' ? 'rta' : null);
    setFreq('never');
    setEndDate('');
  };

  const validate = (): string | null => {
    if (!account) return 'Pick an account';
    if (!amount) return 'Enter an amount';
    if (dir === 'xfer') {
      if (!to) return 'Pick the account the money goes to';
      if (to.id === account.id) return 'Pick two different accounts';
      if (!toAmount) return 'Enter the amount that arrives';
      return null;
    }
    if (splits) {
      if (splits.length < 2) return 'A split needs at least two parts';
      if (splitTotal !== amount)
        return `Split parts add up to ${fmt.num(splitTotal, 2)}, not ${fmt.num(amount, 2)}`;
    }
    if (ovOpen && rateText && !manualRate) return 'Enter the rate as a number';
    return null;
  };

  /** Create or update a schedule (online only: the server enters each occurrence). */
  const saveSchedule = async (): Promise<string> => {
    if (!navigator.onLine) throw new Error('Scheduling needs a connection. Try again when you are online.');
    const repeats = freq !== 'never';
    if (repeats && endDate && endDate < date) throw new Error('The end date is before the first date');
    const common = { frequency: freq, dateNext: date, endDate: repeats && endDate ? endDate : null, memo };
    const body =
      dir === 'xfer'
        ? {
            ...common,
            accountId: account!.id,
            amount: -amount,
            transferAccountId: to!.id,
            transferAmount: to!.currency !== account!.currency ? toAmount : null,
          }
        : {
            ...common,
            accountId: account!.id,
            payeeName: payee,
            amount: signed,
            flag,
            ...(sc?.transfer ? { transferAccountId: null, transferAmount: null } : {}),
            ...(splits
              ? {
                  splits: splits.map((s) => ({
                    categoryId: s.categoryId,
                    amount: (dir === 'in' ? 1 : -1) * Math.abs(parseAmount(s.amount, fmt.locale) ?? 0),
                    memo: s.memo,
                  })),
                }
              : { categoryId, ...(sc?.splits.length ? { splits: [] } : {}) }),
          };
    if (sc) await patch(`/api/scheduled/${sc.id}`, body);
    else await post('/api/scheduled', body);
    invalidateScopes(['transactions', 'accounts']);
    if (sc) return 'Schedule saved';
    return repeats
      ? `Scheduled ${FREQUENCY_LABELS[freq].toLowerCase()} from ${fmt.date(date)} · each one arrives unapproved in the register`
      : `Scheduled for ${fmt.date(date)} · it arrives unapproved in the register`;
  };

  /** Create or update. Returns a toast message. */
  const save = async (): Promise<string> => {
    const err = validate();
    if (err) throw new Error(err);
    localStorage.setItem('koverta.lastAccount', account!.id);
    rememberAccount(account!.id);
    if (isSchedule) return saveSchedule();
    if (dir === 'xfer') {
      const body = {
        fromAccountId: account!.id,
        toAccountId: to!.id,
        fromAmount: amount,
        toAmount,
        date,
        memo,
        clientId: crypto.randomUUID(),
      };
      if (tx?.transfer) await put(`/api/transfers/${tx.transfer.id}`, body);
      else {
        const r = await sendOrQueue({ method: 'POST', path: '/api/transfers', body, label: 'Transfer' });
        if (r.queued) return 'Transfer saved offline · will sync when online';
      }
      invalidateScopes(['transactions', 'plan', 'accounts', 'activity']);
      return `Transferred ${fmt.money(amount, account!.currency)} → ${to!.name}`;
    }
    const body = {
      accountId: account!.id,
      date,
      payeeName: payee,
      amount: signed,
      memo,
      flag,
      rate: account!.currency !== fmt.base ? (manualRate ?? null) : undefined,
      ...(splits
        ? {
            splits: splits.map((s) => ({
              categoryId: s.categoryId,
              amount: (dir === 'in' ? 1 : -1) * Math.abs(parseAmount(s.amount, fmt.locale) ?? 0),
              memo: s.memo,
            })),
          }
        : { categoryId, ...(tx?.splits.length ? { splits: [] } : {}) }),
    };
    const cat = !splits && categoryId && categoryId !== 'rta' ? catById.get(categoryId) : undefined;
    const after = cat
      ? cat.available + base - (tx && tx.categoryId === categoryId ? tx.baseAmount : 0)
      : null;
    if (tx) {
      await patch(`/api/transactions/${tx.id}`, { ...body, approved: true });
      invalidateScopes(['transactions', 'plan', 'accounts', 'activity', 'inbox']);
      return 'Saved';
    }
    const r = await sendOrQueue({
      method: 'POST',
      path: '/api/transactions',
      body: { ...body, clientId: crypto.randomUUID() },
      label: payee || 'Transaction',
    });
    invalidateScopes(['transactions', 'plan', 'accounts', 'activity']);
    const what = `${cat ? cat.name : categoryId === 'rta' ? 'Ready to Assign' : 'Saved'} ${fmt.num(base, 0, dir === 'in' ? 'always' : 'auto')} ${fmt.base}${account!.currency !== fmt.base ? ` (${fmt.money(signed, account!.currency)})` : ''}`;
    if (r.queued) return `Saved offline · ${what} · will sync when online`;
    return `Saved · ${what}${after !== null ? ` → now ${fmt.num(after)}` : ''}`;
  };

  return {
    fmt,
    member,
    accounts,
    budgetAccounts,
    dir,
    setDir: (d: Dir) => {
      setDir(d);
      if (d === 'in' && !categoryId) setCategoryId('rta');
      if (d === 'out' && categoryId === 'rta') setCategoryId(null);
    },
    account,
    acctId,
    onPickAccount,
    raw,
    setRaw,
    amount,
    to,
    toId,
    setToId,
    toRaw,
    setToRaw,
    toAmount,
    payee,
    setPayee,
    categoryId,
    setCategoryId,
    date,
    setDate,
    memo,
    setMemo,
    flag,
    setFlag,
    splits,
    setSplits,
    splitTotal,
    rateText,
    setRateText,
    ovOpen,
    setOvOpen,
    nbsRate,
    manualRate,
    base,
    groups,
    catById,
    save,
    reset,
    validate,
    rates,
    today,
    freq,
    setFreq,
    endDate,
    setEndDate,
    isSchedule,
    schedule: sc,
  };
}
type Form = ReturnType<typeof useTxForm>;

function ConversionLine({ f, desktop }: { f: Form; desktop?: boolean }) {
  const { fmt, account } = f;
  if (!account || f.dir === 'xfer') return null;
  if (account.currency === fmt.base) return <span>Budget currency · no conversion</span>;
  const shownRate = f.manualRate ?? f.nbsRate;
  const overridden = f.manualRate !== null && f.manualRate !== f.nbsRate;
  return (
    <>
      {desktop ? <Icon name="swap" size={15} /> : null}≈ <b>{fmt.money(Math.abs(f.base), fmt.base)}</b> at{' '}
      {overridden ? 'your rate' : "today's rate"} ({fmt.rate(shownRate)})
      {overridden ? (
        <span className="muted"> · NBS {fmt.rate(f.nbsRate)}</span>
      ) : desktop && f.rates ? (
        <span className="muted"> · NBS middle, {fmt.date(f.rates.current.date)}</span>
      ) : null}
      {f.ovOpen ? (
        <input
          className="rate-in"
          value={f.rateText}
          onChange={(e) => f.setRateText(e.target.value)}
          inputMode="decimal"
          aria-label="Exchange rate"
          autoFocus
        />
      ) : (
        <button
          className="rate-btn"
          onClick={() => {
            f.setOvOpen(true);
            f.setRateText(fmt.rate(f.nbsRate));
          }}
        >
          {desktop ? 'Override rate' : 'Change rate'}
        </button>
      )}
      {f.ovOpen ? (
        <button className="rate-btn" onClick={() => (f.setOvOpen(false), f.setRateText(''))}>
          Reset
        </button>
      ) : null}
    </>
  );
}

function PayeeInput({
  f,
  className,
  inputRef,
}: {
  f: Form;
  className?: string;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const payees = usePayees().data ?? [];
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false), open);
  const q = f.payee.trim().toLowerCase();
  const matches = payees.filter((p) => !q || p.name.toLowerCase().includes(q)).slice(0, 6);
  const exact = payees.some((p) => p.name.toLowerCase() === q);
  const options = [
    ...(q && !exact
      ? [
          {
            id: '__new',
            name: f.payee.trim(),
            lastCategoryId: null,
            lastCategoryName: null,
            lastUsedAt: null,
          },
        ]
      : []),
    ...matches,
  ];
  const pick = (o: (typeof options)[number]) => {
    f.setPayee(o.name);
    if (o.lastCategoryId && !f.categoryId && f.dir === 'out' && f.catById.has(o.lastCategoryId))
      f.setCategoryId(o.lastCategoryId);
    setOpen(false);
  };
  return (
    <div className={`ac ${className ?? ''}`} ref={ref}>
      <input
        ref={inputRef}
        className="input"
        value={f.payee}
        placeholder="Who did you pay?"
        onChange={(e) => {
          f.setPayee(e.target.value);
          setOpen(true);
          setIdx(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (!open || !options.length) return;
          if (e.key === 'ArrowDown') setIdx((i) => Math.min(options.length - 1, i + 1));
          else if (e.key === 'ArrowUp') setIdx((i) => Math.max(0, i - 1));
          else if (e.key === 'Enter' && options[idx]) pick(options[idx]!);
          else if (e.key === 'Escape') setOpen(false);
          else return;
          e.preventDefault();
          e.stopPropagation();
        }}
        aria-label="Payee"
        autoComplete="off"
      />
      {open && options.length ? (
        <div className="ac-list" role="listbox">
          {options.map((o, i) => (
            <div
              key={o.id}
              className={i === idx ? 'on' : ''}
              role="option"
              aria-selected={i === idx}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
              style={{ cursor: 'pointer' }}
            >
              {o.name}{' '}
              <small>
                {o.id === '__new'
                  ? 'new payee'
                  : o.lastCategoryName
                    ? `${o.lastCategoryName}${o.lastUsedAt ? ` · last used ${f.fmt.date(o.lastUsedAt)}` : ''}`
                    : ''}
              </small>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CategoryField({
  f,
  value,
  onChange,
  allowRta,
}: {
  f: Form;
  value: CategoryRef;
  onChange: (v: CategoryRef) => void;
  allowRta: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false), open);
  const c = value && value !== 'rta' ? f.catById.get(value) : undefined;
  const groups: PlanGroupDto[] = allowRta
    ? [
        {
          id: '__in',
          name: 'Inflow',
          hidden: false,
          categories: [
            { id: 'rta', name: 'Ready to Assign', available: 0, state: 'zero' } as PlanCategoryDto,
          ],
        },
        ...f.groups,
      ]
    : f.groups;
  return (
    <div className="ac" ref={ref}>
      <button
        type="button"
        className="fbox"
        style={{ width: '100%', textAlign: 'left' }}
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value === 'rta' ? (
          'Inflow: Ready to Assign'
        ) : c ? (
          c.name
        ) : (
          <span className="muted">Pick a category</span>
        )}
        <span style={{ marginLeft: 'auto' }}>{c ? <CatPill c={c} /> : null}</span>
      </button>
      {open ? (
        <div
          style={{
            position: 'absolute',
            zIndex: 5,
            left: 0,
            right: 0,
            top: 'calc(100% + 4px)',
            boxShadow: 'var(--shadow-lg)',
            borderRadius: 12,
          }}
        >
          <CategoryPicker
            groups={groups}
            current={typeof value === 'string' ? value : null}
            onPick={(id) => {
              onChange(id);
              setOpen(false);
            }}
            onClose={() => setOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

function SplitEditor({ f }: { f: Form }) {
  const { fmt } = f;
  const rows = f.splits!;
  const set = (i: number, patch_: Partial<SplitRow>) =>
    f.setSplits(rows.map((r, j) => (j === i ? { ...r, ...patch_ } : r)));
  const left = f.amount - f.splitTotal;
  return (
    <div className="card" style={{ padding: 12, background: 'var(--surface-2)' }}>
      <div className="h4">
        Split
        <span className={left === 0 ? 'pos-t' : 'warn-t'} style={{ textTransform: 'none', letterSpacing: 0 }}>
          {left === 0 ? 'Fully split' : `${fmt.num(left, 2)} left to split`}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.3fr 110px 1fr 30px',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <CategoryField
              f={f}
              value={r.categoryId}
              onChange={(v) => set(i, { categoryId: v })}
              allowRta={f.dir === 'in'}
            />
            <MoneyInput
              className="input r"
              value={r.amount}
              onChange={(text) => set(i, { amount: text })}
              aria-label="Split amount"
              placeholder={left > 0 ? fmt.num(left, 2) : undefined}
            />
            <input
              className="input"
              value={r.memo}
              onChange={(e) => set(i, { memo: e.target.value })}
              placeholder="Memo"
              aria-label="Split memo"
            />
            <button
              className="ib"
              onClick={() => f.setSplits(rows.filter((_, j) => j !== i))}
              aria-label="Remove split part"
            >
              <Icon name="x" size={15} />
            </button>
          </div>
        ))}
      </div>
      <div className="row-gap" style={{ marginTop: 8 }}>
        <button
          className="btn ghost"
          onClick={() =>
            f.setSplits([...rows, { categoryId: null, amount: left > 0 ? fmt.num(left, 2) : '', memo: '' }])
          }
        >
          <Icon name="plus" size={15} />
          Add part
        </button>
        <span className="sp" />
        <button className="btn ghost" onClick={() => f.setSplits(null)}>
          Remove split
        </button>
      </div>
    </div>
  );
}

function FlagPicker({ f }: { f: Form }) {
  return (
    <span className="flags" role="radiogroup" aria-label="Flag">
      {FLAG_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          className={`dot flag-${c} ${f.flag === c ? 'on' : ''}`}
          onClick={() => f.setFlag(f.flag === c ? null : c)}
          aria-label={`${c} flag`}
          aria-checked={f.flag === c}
          role="radio"
        />
      ))}
    </span>
  );
}

/** Cross-currency transfer numbers. The implied rate is always shown as RSD per €. */
function crossRate(f: Form) {
  const { account, to } = f;
  const cross = !!account && !!to && account.currency !== to.currency;
  const eurAmt = account?.currency === 'EUR' ? f.amount : f.toAmount;
  const rsdAmt = account?.currency === 'EUR' ? f.toAmount : f.amount;
  const implied = cross && eurAmt && rsdAmt ? impliedRate(eurAmt, rsdAmt) : 0;
  const spread = implied && f.nbsRate ? Math.abs(implied - f.nbsRate) / f.nbsRate : 0;
  return { cross, implied, spread };
}

function TransferPanel({ f, desktop }: { f: Form; desktop?: boolean }) {
  const { fmt, account, to } = f;
  const [toText, setToText] = useState(f.toRaw ? fmt.num(rawToMinor(f.toRaw), 2) : '');
  const { cross, implied, spread } = crossRate(f);
  return (
    <>
      <div className="xpair">
        <div className="xb">
          <small>
            From {account ? <FlagIcon currency={account.currency} /> : null}
            {desktop ? (
              <select
                className="select"
                style={{ minHeight: 0, padding: '2px 6px', fontSize: 12 }}
                value={f.acctId}
                onChange={(e) => f.onPickAccount(e.target.value)}
                aria-label="From account"
              >
                {f.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            ) : (
              account?.name
            )}
          </small>
          <b>
            −
            {account ? (account.currency === 'EUR' ? fmt.money(f.amount, 'EUR') : fmt.num(f.amount, 2)) : '0'}
          </b>
        </div>
        <span className="arr">
          <Icon name="chevR" size={22} />
        </span>
        <div className="xb" style={{ borderColor: 'var(--brand-2)' }}>
          <small>
            To {to ? <FlagIcon currency={to.currency} /> : null}
            <select
              className="select"
              style={{ minHeight: 0, padding: '2px 6px', fontSize: 12 }}
              value={f.toId}
              onChange={(e) => f.setToId(e.target.value)}
              aria-label="To account"
            >
              <option value="">Pick account</option>
              {f.accounts
                .filter((a) => a.id !== f.acctId)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
          </small>
          {cross && desktop ? (
            <MoneyInput
              className="input r"
              style={{ font: '500 22px var(--serif)', marginTop: 4 }}
              value={toText}
              onChange={(text) => {
                setToText(text);
                f.setToRaw(minorToRaw(Math.abs(parseAmount(text, fmt.locale) ?? 0)));
              }}
              aria-label="Amount that arrives"
            />
          ) : (
            <b>
              +{to ? (to.currency === 'EUR' ? fmt.money(f.toAmount, 'EUR') : fmt.num(f.toAmount, 2)) : '0'}
            </b>
          )}
        </div>
      </div>
      {cross ? (
        <div
          className="card"
          style={{
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontSize: 13,
            background: 'var(--surface-2)',
          }}
        >
          <div className="kv">
            <span>Implied rate</span>
            <b>{implied ? `${fmt.rate(implied)} RSD / €` : '—'}</b>
          </div>
          <div className="kv">
            <span>NBS middle rate{f.rates ? `, ${fmt.date(f.rates.current.date)}` : ''}</span>
            <b>{fmt.rate(f.nbsRate)}</b>
          </div>
          <div className="kv">
            <span>Spread vs. NBS</span>
            <b className={spread <= 0.005 ? 'pos-t' : 'warn-t'}>
              {implied ? formatPercent(spread, fmt.locale) : '—'}
            </b>
          </div>
        </div>
      ) : null}
      <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
        {cross
          ? 'Both sides are stored exactly as entered. Neither side touches a category, because this is a transfer between two budget accounts. Any difference from the NBS rate only shows in the FX revaluation line.'
          : 'Moving money between your own accounts never touches a category.'}
      </div>
    </>
  );
}

/** Repeat + end date (scheduled-transactions.md §5). Hidden when editing a transaction. */
function RepeatFields({ f }: { f: Form }) {
  const repeats = f.freq !== 'never';
  return (
    <>
      <label className="field">
        Repeat
        <select
          className="select"
          value={f.freq}
          onChange={(e) => f.setFreq(e.target.value as Frequency)}
          aria-label="Repeat"
        >
          {FREQUENCIES.map((fr) => (
            <option key={fr} value={fr}>
              {FREQUENCY_LABELS[fr]}
            </option>
          ))}
        </select>
      </label>
      {repeats ? (
        <label className="field">
          Ends · optional
          <input
            className="input"
            type="date"
            value={f.endDate}
            min={f.date}
            onChange={(e) => f.setEndDate(e.target.value)}
            aria-label="End date"
          />
        </label>
      ) : (
        <div className="field sched-hint">
          {f.isSchedule ? (
            <span>
              <Icon name="cal" size={14} /> A future date is saved as a scheduled transaction
            </span>
          ) : null}
        </div>
      )}
    </>
  );
}

// ───────────────────────── Desktop dialog ─────────────────────────

export function TransactionDialog({ onClose, ...init }: FormInit & { onClose: () => void }) {
  const f = useTxForm(init);
  const { toast } = useToast();
  const [err, setErr] = useState<string | null>(null);
  const amountRef = useRef<HTMLInputElement>(null);
  const [amountText, setAmountText] = useState(f.raw ? f.fmt.num(rawToMinor(f.raw), 2) : '');
  const editing = !!init.tx;
  const editingSeries = !!init.schedule;
  const submit = useCallback(
    async (again = false) => {
      setErr(null);
      try {
        toast(await f.save());
        if (again) {
          f.reset();
          setAmountText('');
          amountRef.current?.focus();
        } else onClose();
      } catch (e) {
        setErr(e instanceof Error && !('status' in e) ? e.message : errorMessage(e));
      }
    },
    [f, onClose, toast],
  );
  const removeSeries = async () => {
    if (!init.schedule || !confirm('Delete this scheduled series? Transactions already entered stay.'))
      return;
    try {
      await del(`/api/scheduled/${init.schedule.id}`);
      invalidateScopes(['transactions', 'accounts']);
      toast('Schedule deleted');
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  const remove = async () => {
    if (!init.tx || !confirm('Delete this transaction?')) return;
    try {
      await del(`/api/transactions/${init.tx.id}`);
      invalidateScopes(['transactions', 'plan', 'accounts', 'activity', 'inbox']);
      toast('Deleted');
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void submit();
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [submit]);
  const { fmt, account } = f;
  return (
    <Dialog
      title={
        editingSeries
          ? 'Edit scheduled transaction'
          : editing
            ? init.tx!.approved
              ? 'Edit transaction'
              : 'Review transaction'
            : f.isSchedule
              ? f.dir === 'xfer'
                ? 'Schedule transfer'
                : 'Schedule transaction'
              : f.dir === 'xfer'
                ? 'Transfer'
                : 'Add transaction'
      }
      onClose={onClose}
      size="xl"
      footer={
        <>
          {f.isSchedule ? (
            <span className="muted sched-foot">
              <Icon name="cal" size={14} />
              Each occurrence arrives unapproved in the register
            </span>
          ) : (
            <>
              <Avatar id={init.tx?.createdBy ?? f.member.id} />
              <span className="muted" style={{ fontSize: 12 }}>
                Added by {editing ? 'them' : f.member.displayName}
              </span>
            </>
          )}
          {editing ? (
            <button className="btn ghost danger" onClick={remove}>
              Delete
            </button>
          ) : null}
          {editingSeries ? (
            <button className="btn ghost danger" onClick={removeSeries}>
              Delete series
            </button>
          ) : null}
          <span className="sp" />
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          {!editing && !editingSeries ? (
            <button className="btn" onClick={() => submit(true)}>
              Save &amp; add another
            </button>
          ) : null}
          <button className="btn primary" onClick={() => submit()}>
            {editing && !init.tx!.approved ? 'Approve' : 'Save'} <Kbd inverse>⌘↵</Kbd>
          </button>
        </>
      }
    >
      {!editing || !init.tx!.transfer ? (
        <Seg
          value={f.dir}
          onChange={f.setDir}
          label="Direction"
          options={[
            ['out', 'Outflow'],
            ['in', 'Inflow'],
            ...(!editing ? ([['xfer', 'Transfer']] as [Dir, string][]) : []),
          ]}
        />
      ) : null}
      <div className="bigamt">
        <span className="v" style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ color: f.dir === 'in' ? 'var(--pos)' : undefined }}>
            {f.dir === 'in' ? '+' : '−'}
          </span>
          {account?.currency === 'EUR' ? '€' : null}
          <MoneyInput
            ref={amountRef}
            autoFocus
            value={amountText}
            onChange={(text) => {
              setAmountText(text);
              const v = parseAmount(text, fmt.locale);
              f.setRaw(v === null ? '' : minorToRaw(Math.abs(v)));
            }}
            onBlur={() => amountText && setAmountText(fmt.num(f.amount, 2))}
            aria-label="Amount"
            style={{
              font: 'inherit',
              border: 0,
              outline: 'none',
              background: 'transparent',
              width: '100%',
              color: f.dir === 'in' ? 'var(--pos)' : 'var(--ink)',
            }}
          />
        </span>
        {account ? <CurrencyChip currency={account.currency} /> : null}
      </div>
      {f.dir === 'xfer' ? (
        <TransferPanel f={f} desktop />
      ) : (
        <div className="conv">
          <ConversionLine f={f} desktop />
        </div>
      )}
      <div className="fgrid">
        {f.dir !== 'xfer' ? (
          <label className="field">
            Account
            <select className="select" value={f.acctId} onChange={(e) => f.onPickAccount(e.target.value)}>
              {f.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · {fmt.money(a.working, a.currency)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="field">
          {f.isSchedule ? 'Next date' : 'Date'}
          <input
            className="input"
            type="date"
            value={f.date}
            onChange={(e) => e.target.value && f.setDate(e.target.value)}
          />
        </label>
        {!editing ? <RepeatFields f={f} /> : null}
        {f.dir !== 'xfer' ? (
          <>
            <label className="field">
              Payee
              <PayeeInput f={f} />
            </label>
            {!f.splits ? (
              <div className="field">
                Category
                <CategoryField
                  f={f}
                  value={f.categoryId}
                  onChange={f.setCategoryId}
                  allowRta={f.dir === 'in'}
                />
              </div>
            ) : (
              <div />
            )}
          </>
        ) : null}
        <label className={`field ${f.dir === 'xfer' ? '' : 'wide'}`}>
          Memo
          <input
            className="input"
            value={f.memo}
            onChange={(e) => f.setMemo(e.target.value)}
            maxLength={500}
          />
        </label>
      </div>
      {f.splits ? <SplitEditor f={f} /> : null}
      {f.dir !== 'xfer' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Flag</span>
          <FlagPicker f={f} />
          <span style={{ flex: 1 }} />
          {!f.splits ? (
            <button
              className="btn"
              onClick={() =>
                f.setSplits([
                  { categoryId: f.categoryId, amount: f.amount ? fmt.num(f.amount, 2) : '', memo: '' },
                  { categoryId: null, amount: '', memo: '' },
                ])
              }
            >
              <Icon name="split" size={15} />
              Split
            </button>
          ) : null}
        </div>
      ) : null}
      {err ? (
        <div className="err-t" role="alert">
          {err}
        </div>
      ) : null}
    </Dialog>
  );
}

// ───────────────────────── Mobile page ─────────────────────────
// A single screen that fits above the system keyboard: the amount is a real input (decimal keyboard), the
// four main fields are rows, and the rarely used ones (memo, flag, split, repeat) sit behind chips.

const RECENT_ACCOUNTS_KEY = 'koverta.recentAccounts';
const recentAccountIds = (): string[] => {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(RECENT_ACCOUNTS_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};
/** Most recently used first, so the picker can offer them on top. */
function rememberAccount(id: string) {
  const next = [id, ...recentAccountIds().filter((x) => x !== id)].slice(0, 6);
  localStorage.setItem(RECENT_ACCOUNTS_KEY, JSON.stringify(next));
}

const KIND_ORDER: AccountKind[] = ['cash', 'checking', 'savings', 'credit', 'asset', 'liability', 'loan'];
const KIND_LABEL: Record<AccountKind, string> = {
  cash: 'Cash',
  checking: 'Checking',
  savings: 'Savings',
  credit: 'Credit',
  asset: 'Assets',
  liability: 'Liabilities',
  loan: 'Loans',
};
const fold = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
const accountBalance = (fmt: Fmt, a: AccountDto) =>
  a.currency === 'EUR' ? fmt.money(a.working, 'EUR') : fmt.num(a.working);

/** Account picker: recent accounts on top, then groups by kind; search appears once the list gets long. */
function AccountSheet({
  title,
  accounts,
  value,
  onPick,
  onClose,
}: {
  title: string;
  accounts: AccountDto[];
  value: string;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const { fmt } = useHousehold();
  const [q, setQ] = useState('');
  const query = fold(q.trim());
  const shown = accounts.filter((a) => !query || fold(`${a.name} ${a.institution}`).includes(query));
  const recents = query
    ? []
    : recentAccountIds()
        .flatMap((id) => accounts.find((a) => a.id === id) ?? [])
        .slice(0, 3);
  const groups = KIND_ORDER.map((kind) => ({ kind, list: shown.filter((a) => a.kind === kind) })).filter(
    (g) => g.list.length,
  );
  const row = (a: AccountDto) => (
    <button
      key={a.id}
      type="button"
      className={`ar ${a.id === value ? 'on' : ''}`}
      onClick={() => {
        onPick(a.id);
        onClose();
      }}
    >
      <FlagIcon currency={a.currency} />
      <span className="n">{a.name}</span>
      <span className="bl">{accountBalance(fmt, a)}</span>
      <span className="ck">{a.id === value ? <Icon name="check" size={16} /> : null}</span>
    </button>
  );
  return (
    <Sheet onClose={onClose} label={title} className="acct-sheet">
      <div className="sh-h">
        <b>{title}</b>
        <span className="muted" style={{ fontSize: 12 }}>
          {accounts.length} accounts
        </span>
      </div>
      {accounts.length > 7 ? (
        <label className="srch">
          <Icon name="search" size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search accounts"
            aria-label="Search accounts"
            autoComplete="off"
          />
        </label>
      ) : null}
      {recents.length ? (
        <>
          <div className="gh">Recent</div>
          {recents.map(row)}
        </>
      ) : null}
      {groups.map((g) => (
        <div key={g.kind}>
          <div className="gh">{KIND_LABEL[g.kind]}</div>
          {g.list.map(row)}
        </div>
      ))}
      {!shown.length ? <div className="muted gh">No account matches “{q.trim()}”</div> : null}
    </Sheet>
  );
}

function AccountField({
  label,
  account,
  placeholder,
  onClick,
}: {
  label: string;
  account: AccountDto | undefined;
  placeholder: string;
  onClick: () => void;
}) {
  const { fmt } = useHousehold();
  return (
    <button type="button" className="m-f m-btn" onClick={onClick}>
      <span>{label}</span>
      <div className="v">
        {account ? (
          <>
            <span className="m-fa">
              <FlagIcon currency={account.currency} />
              {account.name}
            </span>
            <span className="muted">
              {accountBalance(fmt, account)} <Icon name="chevD" size={14} />
            </span>
          </>
        ) : (
          <>
            <span className="muted">{placeholder}</span>
            <Icon name="chevD" size={14} />
          </>
        )}
      </div>
    </button>
  );
}

export function AddTransactionPage() {
  const [sp] = useSearchParams();
  const init = {
    accountId: sp.get('account') ?? undefined,
    mode: (sp.get('mode') as Dir | null) ?? undefined,
  };
  const accounts = useAccounts();
  if (accounts.isPending) return null;
  return <AddMobile {...init} />;
}

type MobileSheet = 'account' | 'to' | 'category' | 'memo' | 'flag' | 'repeat';

function AddMobile(init: FormInit) {
  const f = useTxForm(init);
  const { fmt, account } = f;
  const { toast } = useToast();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<MobileSheet | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [amountText, setAmountText] = useState('');
  const [toText, setToText] = useState('');
  const amountRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const payeeRef = useRef<HTMLInputElement>(null);
  const xfer = f.dir === 'xfer';
  const { cross, implied, spread } = crossRate(f);
  const c = f.categoryId && f.categoryId !== 'rta' ? f.catById.get(f.categoryId) : undefined;
  const close = () => setSheet(null);

  // Open the number keyboard right away, and again when the direction changes the amount input.
  useEffect(() => {
    amountRef.current?.focus();
  }, [f.dir]);

  const onAmount = (text: string) => {
    setAmountText(text);
    const v = parseAmount(text, fmt.locale);
    f.setRaw(v === null ? '' : minorToRaw(Math.abs(v)));
  };
  const onToAmount = (text: string) => {
    setToText(text);
    const v = parseAmount(text, fmt.locale);
    f.setToRaw(v === null ? '' : minorToRaw(Math.abs(v)));
  };
  const amountProps = {
    value: amountText,
    onChange: onAmount,
    onBlur: () => amountText && setAmountText(fmt.num(f.amount, 2)),
    onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (!xfer) payeeRef.current?.focus();
      else if (cross) toRef.current?.focus();
      else e.currentTarget.blur();
    },
    enterKeyHint: 'next' as const,
    'aria-label': 'Amount',
  };

  const save = async () => {
    setErr(null);
    try {
      toast(await f.save(), { ms: 3600 });
      f.reset();
      setAmountText('');
      setToText('');
      amountRef.current?.focus();
    } catch (e) {
      setErr(e instanceof Error && !('status' in e) ? e.message : errorMessage(e));
    }
  };
  const openSplit = () =>
    f.setSplits([
      { categoryId: f.categoryId, amount: f.amount ? fmt.num(f.amount, 2) : '', memo: '' },
      { categoryId: null, amount: '', memo: '' },
    ]);
  const title = f.isSchedule
    ? xfer
      ? 'Scheduled transfer'
      : 'New scheduled'
    : xfer
      ? 'New transfer'
      : 'New transaction';

  const dateRow = (
    <div className="m-f">
      <span>{f.isSchedule ? 'Next date' : 'Date'}</span>
      <div className="v">
        <input
          type="date"
          value={f.date}
          onChange={(e) => e.target.value && f.setDate(e.target.value)}
          aria-label="Date"
        />
      </div>
    </div>
  );

  return (
    <div className="add-page">
      <div className="add-top">
        <button onClick={() => navigate(-1)} aria-label="Cancel">
          <Icon name="x" size={22} />
        </button>
        <b>{title}</b>
        <button className="add-save" onClick={save}>
          Save
        </button>
      </div>
      <Seg
        full
        value={f.dir}
        onChange={f.setDir}
        label="Direction"
        options={[
          ['out', 'Outflow'],
          ['in', 'Inflow'],
          ['xfer', 'Transfer'],
        ]}
      />
      {xfer ? (
        <>
          <div className="m-fields">
            <AccountField
              label="From"
              account={account}
              placeholder="Pick account"
              onClick={() => setSheet('account')}
            />
            <AccountField
              label="To"
              account={f.to}
              placeholder="Pick account"
              onClick={() => setSheet('to')}
            />
          </div>
          <div className={`add-pair ${cross ? '' : 'one'}`}>
            <label className="pb">
              <small>{cross ? 'You send' : 'Amount'}</small>
              <span className="pv">
                <MoneyInput ref={amountRef} {...amountProps} />
                <i>{account?.currency}</i>
              </span>
            </label>
            {cross ? (
              <label className="pb">
                <small>Arrives</small>
                <span className="pv">
                  <MoneyInput
                    ref={toRef}
                    value={toText}
                    onChange={onToAmount}
                    onBlur={() => toText && setToText(fmt.num(f.toAmount, 2))}
                    aria-label="Amount that arrives"
                  />
                  <i>{f.to!.currency}</i>
                </span>
              </label>
            ) : null}
          </div>
          {cross ? (
            <div className="add-rate">
              Implied <b>{implied ? fmt.rate(implied) : '—'}</b> RSD/€ · NBS {fmt.rate(f.nbsRate)} · spread{' '}
              <b className={spread <= 0.005 ? 'pos-t' : 'warn-t'}>
                {implied ? formatPercent(spread, fmt.locale) : '—'}
              </b>
            </div>
          ) : null}
          <div className="m-fields">{dateRow}</div>
        </>
      ) : (
        <>
          <div className="add-amt">
            <label className={`v ${f.dir === 'in' ? 'in' : 'out'}`}>
              <span aria-hidden="true">{f.dir === 'in' ? '+' : '−'}</span>
              <MoneyInput ref={amountRef} {...amountProps} />
              <span className="u">{account?.currency ?? fmt.base}</span>
            </label>
            <div className={`add-conv ${account?.currency === fmt.base ? 'plain' : ''}`}>
              <ConversionLine f={f} />
            </div>
          </div>
          <div className="m-fields">
            <AccountField
              label="Account"
              account={account}
              placeholder="Pick account"
              onClick={() => setSheet('account')}
            />
            <div className="m-f">
              <span>Payee</span>
              <div className="v" style={{ display: 'block' }}>
                <PayeeInput f={f} className="m-payee" inputRef={payeeRef} />
              </div>
            </div>
            {!f.splits ? (
              <button type="button" className="m-f m-btn" onClick={() => setSheet('category')}>
                <span>Category</span>
                <div className="v">
                  <span>
                    {f.categoryId === 'rta' ? (
                      'Ready to Assign'
                    ) : c ? (
                      c.name
                    ) : (
                      <span className="muted">Pick a category</span>
                    )}
                  </span>
                  {c ? <CatPill c={c} /> : null}
                </div>
              </button>
            ) : null}
            {dateRow}
          </div>
        </>
      )}
      <div className="add-chips">
        <button type="button" className={`add-chip ${f.memo ? 'on' : ''}`} onClick={() => setSheet('memo')}>
          <Icon name="memo" size={15} />
          {f.memo ? <span className="t">{f.memo}</span> : 'Memo'}
        </button>
        {!xfer ? (
          <button type="button" className={`add-chip ${f.flag ? 'on' : ''}`} onClick={() => setSheet('flag')}>
            {f.flag ? <span className={`dot flag-${f.flag}`} /> : <Icon name="flag" size={15} />}
            Flag
          </button>
        ) : null}
        {!xfer && !f.splits ? (
          <button type="button" className="add-chip" onClick={openSplit}>
            <Icon name="split" size={15} />
            Split
          </button>
        ) : null}
        <button
          type="button"
          className={`add-chip ${f.freq !== 'never' ? 'on' : ''}`}
          onClick={() => setSheet('repeat')}
        >
          <Icon name="cal" size={15} />
          {f.freq !== 'never' ? FREQUENCY_LABELS[f.freq] : 'Repeat'}
        </button>
      </div>
      {f.splits ? (
        <div style={{ margin: '10px 12px 0' }}>
          <SplitEditor f={f} />
        </div>
      ) : null}
      {f.isSchedule ? (
        <div className="add-note">
          <Icon name="cal" size={14} /> Saved as a schedule · each occurrence arrives unapproved in the
          register
        </div>
      ) : null}
      {err ? (
        <div className="err-t" role="alert" style={{ textAlign: 'center', padding: '10px 16px 0' }}>
          {err}
        </div>
      ) : null}

      {sheet === 'account' || sheet === 'to' ? (
        <AccountSheet
          title={
            sheet === 'to' ? 'To account' : xfer ? 'From account' : f.dir === 'in' ? 'Paid into' : 'Paid from'
          }
          accounts={
            sheet === 'to'
              ? f.accounts.filter((a) => a.id !== f.acctId)
              : f.accounts.filter((a) => a.onBudget || xfer)
          }
          value={sheet === 'to' ? f.toId : f.acctId}
          onPick={(id) => {
            if (sheet === 'to') f.setToId(id);
            else {
              f.onPickAccount(id);
              if (id === f.toId) f.setToId('');
            }
          }}
          onClose={close}
        />
      ) : null}
      {sheet === 'category' ? (
        <Sheet onClose={close} label="Category" className="cat-sheet">
          <div className="sh-h">
            <b>Category</b>
            <span className="muted" style={{ fontSize: 12 }}>
              Available shown
            </span>
          </div>
          {f.dir === 'in' ? (
            <button
              className={`o ${f.categoryId === 'rta' ? 'on' : ''}`}
              onClick={() => (f.setCategoryId('rta'), close())}
            >
              <span>Inflow: Ready to Assign</span>
            </button>
          ) : null}
          {f.groups.map((g) => (
            <div key={g.id}>
              <div className="g">{g.name}</div>
              {g.categories.map((x) => (
                <button
                  key={x.id}
                  className={`o ${x.id === f.categoryId ? 'on' : ''}`}
                  onClick={() => (f.setCategoryId(x.id), close())}
                >
                  <span>{x.name}</span>
                  <CatPill c={x} />
                </button>
              ))}
            </div>
          ))}
        </Sheet>
      ) : null}
      {sheet === 'memo' ? (
        <Sheet onClose={close} label="Memo">
          <div className="sh-h">
            <b>Memo</b>
          </div>
          <input
            className="input"
            style={{ width: '100%', marginTop: 12 }}
            value={f.memo}
            onChange={(e) => f.setMemo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && close()}
            placeholder="Add a note"
            maxLength={500}
            enterKeyHint="done"
            aria-label="Memo"
            autoFocus
          />
          <button className="btn primary big" style={{ width: '100%', marginTop: 12 }} onClick={close}>
            Done
          </button>
        </Sheet>
      ) : null}
      {sheet === 'flag' ? (
        <Sheet onClose={close} label="Flag">
          <div className="sh-h">
            <b>Flag</b>
          </div>
          {FLAG_COLORS.map((col) => (
            <button
              key={col}
              type="button"
              className={`ar ${f.flag === col ? 'on' : ''}`}
              onClick={() => {
                f.setFlag(f.flag === col ? null : col);
                close();
              }}
            >
              <span className={`dot lg flag-${col}`} />
              <span className="n" style={{ textTransform: 'capitalize' }}>
                {col}
              </span>
              <span className="ck">{f.flag === col ? <Icon name="check" size={16} /> : null}</span>
            </button>
          ))}
        </Sheet>
      ) : null}
      {sheet === 'repeat' ? (
        <Sheet onClose={close} label="Repeat">
          <div className="sh-h">
            <b>Repeat</b>
          </div>
          <div style={{ display: 'grid', gap: 12, margin: '12px 0' }}>
            <RepeatFields f={f} />
          </div>
          <button className="btn primary big" style={{ width: '100%' }} onClick={close}>
            Done
          </button>
        </Sheet>
      ) : null}
    </div>
  );
}

export type { Fmt };
